// YatraNest Nepal — Shared Pricing Engine
// Supports: fixed, per-person, per-child, per-room, per-night, per-hour, per-day, per-km, per-trip
// Seasonal/weekend/peak pricing, add-ons, coupons, discount, tax, service fee, commission
// Rules: Never trust browser total — always revalidate server-side. Prevent negative values.

export type PricingModel =
  | 'fixed' |'per_person' |'per_child' |'per_room' |'per_night' |'per_hour' |'per_day' |'per_km' |'per_trip';

export type ServiceType =
  | 'package' |'hotel' |'guide' |'vehicle' |'flight' |'bus' |'train' |'event' |'restaurant';

export interface PricingRule {
  model: PricingModel;
  baseRate: number; // NPR
  currency?: string; // default 'NPR'
  // Multipliers
  seasonalMultiplier?: number; // e.g. 1.2 for peak season
  weekendMultiplier?: number;  // e.g. 1.15 for weekends
  peakMultiplier?: number;     // e.g. 1.3 for peak dates
  // Rates
  childRate?: number;          // per child (if different from adult)
  extraBedRate?: number;       // per extra bed per night
  extraGuestRate?: number;     // per extra guest beyond room capacity
  driverRate?: number;         // per day for driver
  nightSurchargeRate?: number; // per night surcharge
  tollRate?: number;           // flat toll
  parkingRate?: number;        // flat parking
  waitingRate?: number;        // per hour waiting
  returnTripMultiplier?: number; // e.g. 0.8 for return trip discount
}

export interface AddOn {
  id: string;
  label: string;
  amount: number;
  quantity?: number;
}

export interface CouponResult {
  code: string;
  discountType: 'percent' | 'fixed';
  discountValue: number;
  discountAmount: number;
  valid: boolean;
  message?: string;
}

export interface PriceInput {
  rule: PricingRule;
  // Quantities
  adults?: number;
  children?: number;
  rooms?: number;
  nights?: number;
  hours?: number;
  days?: number;
  distanceKm?: number;
  quantity?: number;
  // Options
  extraBeds?: number;
  extraGuests?: number;
  driverRequired?: boolean;
  isRoundTrip?: boolean;
  nightSurcharge?: boolean;
  tollIncluded?: boolean;
  parkingIncluded?: boolean;
  waitingHours?: number;
  // Pricing adjustments
  isSeasonal?: boolean;
  isWeekend?: boolean;
  isPeak?: boolean;
  // Add-ons
  addOns?: AddOn[];
  // Discounts
  coupon?: CouponResult;
  manualDiscountPercent?: number;
  manualDiscountAmount?: number;
  // Tax & fees
  taxPercent?: number;       // default 13 (Nepal VAT)
  serviceFeePercent?: number; // default 2
  providerCommissionPercent?: number; // default 10
}

export interface PriceBreakdown {
  // Base
  baseAmount: number;
  // Surcharges
  seasonalSurcharge: number;
  weekendSurcharge: number;
  peakSurcharge: number;
  extraBedCharge: number;
  extraGuestCharge: number;
  driverCharge: number;
  nightSurchargeAmount: number;
  tollCharge: number;
  parkingCharge: number;
  waitingCharge: number;
  returnTripDiscount: number;
  // Add-ons
  addOnsTotal: number;
  addOnsDetail: AddOn[];
  // Subtotal before discounts
  subtotal: number;
  // Discounts
  couponDiscount: number;
  manualDiscount: number;
  totalDiscount: number;
  // After discount
  discountedSubtotal: number;
  // Tax & fees
  taxAmount: number;
  taxPercent: number;
  serviceFeeAmount: number;
  serviceFeePercent: number;
  providerCommission: number;
  providerCommissionPercent: number;
  // Final
  finalTotal: number;
  currency: string;
  // Meta
  priceModel: PricingModel;
  breakdown: string[]; // human-readable lines
}

const DEFAULT_TAX_PERCENT = 13;
const DEFAULT_SERVICE_FEE_PERCENT = 2;
const DEFAULT_COMMISSION_PERCENT = 10;

function clamp(val: number): number {
  return Math.max(0, Math.round(val));
}

export function calculatePrice(input: PriceInput): PriceBreakdown {
  const { rule } = input;
  const currency = rule.currency || 'NPR';
  const adults = Math.max(1, input.adults || 1);
  const children = Math.max(0, input.children || 0);
  const rooms = Math.max(1, input.rooms || 1);
  const nights = Math.max(1, input.nights || 1);
  const hours = Math.max(0, input.hours || 0);
  const days = Math.max(1, input.days || 1);
  const distanceKm = Math.max(0, input.distanceKm || 0);
  const quantity = Math.max(1, input.quantity || 1);

  // ── Base amount by model ──────────────────────────────────────────────────
  let baseAmount = 0;
  switch (rule.model) {
    case 'fixed':
      baseAmount = rule.baseRate;
      break;
    case 'per_person':
      baseAmount = rule.baseRate * adults;
      if (rule.childRate !== undefined) {
        baseAmount += rule.childRate * children;
      } else {
        baseAmount += (rule.baseRate * 0.5) * children; // default 50% for children
      }
      break;
    case 'per_child':
      baseAmount = (rule.childRate || rule.baseRate) * children;
      break;
    case 'per_room':
      baseAmount = rule.baseRate * rooms;
      break;
    case 'per_night':
      baseAmount = rule.baseRate * nights * rooms;
      break;
    case 'per_hour':
      baseAmount = rule.baseRate * Math.max(1, hours);
      break;
    case 'per_day':
      baseAmount = rule.baseRate * days;
      break;
    case 'per_km':
      baseAmount = rule.baseRate * distanceKm;
      break;
    case 'per_trip':
      baseAmount = rule.baseRate * quantity;
      break;
    default:
      baseAmount = rule.baseRate;
  }

  // ── Seasonal / weekend / peak multipliers ─────────────────────────────────
  let seasonalSurcharge = 0;
  let weekendSurcharge = 0;
  let peakSurcharge = 0;

  if (input.isSeasonal && rule.seasonalMultiplier && rule.seasonalMultiplier > 1) {
    seasonalSurcharge = clamp(baseAmount * (rule.seasonalMultiplier - 1));
  }
  if (input.isWeekend && rule.weekendMultiplier && rule.weekendMultiplier > 1) {
    weekendSurcharge = clamp(baseAmount * (rule.weekendMultiplier - 1));
  }
  if (input.isPeak && rule.peakMultiplier && rule.peakMultiplier > 1) {
    peakSurcharge = clamp(baseAmount * (rule.peakMultiplier - 1));
  }

  // ── Extra charges ─────────────────────────────────────────────────────────
  const extraBedCharge = clamp((rule.extraBedRate || 0) * (input.extraBeds || 0) * nights);
  const extraGuestCharge = clamp((rule.extraGuestRate || 0) * (input.extraGuests || 0) * nights);
  const driverCharge = input.driverRequired ? clamp((rule.driverRate || 800) * days) : 0;
  const nightSurchargeAmount = input.nightSurcharge ? clamp(rule.nightSurchargeRate || 500) : 0;
  const tollCharge = input.tollIncluded ? clamp(rule.tollRate || 0) : 0;
  const parkingCharge = input.parkingIncluded ? clamp(rule.parkingRate || 0) : 0;
  const waitingCharge = clamp((rule.waitingRate || 0) * (input.waitingHours || 0));

  // ── Return trip discount ──────────────────────────────────────────────────
  let returnTripDiscount = 0;
  if (input.isRoundTrip && rule.returnTripMultiplier && rule.returnTripMultiplier < 1) {
    returnTripDiscount = clamp(baseAmount * (1 - rule.returnTripMultiplier));
  }

  // ── Add-ons ───────────────────────────────────────────────────────────────
  const addOnsDetail = (input.addOns || []).map(a => ({
    ...a,
    amount: clamp(a.amount * (a.quantity || 1)),
  }));
  const addOnsTotal = addOnsDetail.reduce((s, a) => s + a.amount, 0);

  // ── Subtotal ──────────────────────────────────────────────────────────────
  const subtotal = clamp(
    baseAmount +
    seasonalSurcharge + weekendSurcharge + peakSurcharge +
    extraBedCharge + extraGuestCharge +
    driverCharge + nightSurchargeAmount + tollCharge + parkingCharge + waitingCharge -
    returnTripDiscount +
    addOnsTotal
  );

  // ── Discounts ─────────────────────────────────────────────────────────────
  let couponDiscount = 0;
  if (input.coupon?.valid) {
    if (input.coupon.discountType === 'percent') {
      couponDiscount = clamp(subtotal * (input.coupon.discountValue / 100));
    } else {
      couponDiscount = clamp(input.coupon.discountAmount);
    }
  }

  let manualDiscount = 0;
  if (input.manualDiscountPercent) {
    manualDiscount = clamp(subtotal * (input.manualDiscountPercent / 100));
  } else if (input.manualDiscountAmount) {
    manualDiscount = clamp(input.manualDiscountAmount);
  }

  const totalDiscount = couponDiscount + manualDiscount;
  const discountedSubtotal = clamp(subtotal - totalDiscount);

  // ── Tax & fees ────────────────────────────────────────────────────────────
  const taxPct = input.taxPercent ?? DEFAULT_TAX_PERCENT;
  const svcPct = input.serviceFeePercent ?? DEFAULT_SERVICE_FEE_PERCENT;
  const commPct = input.providerCommissionPercent ?? DEFAULT_COMMISSION_PERCENT;

  const taxAmount = clamp(discountedSubtotal * (taxPct / 100));
  const serviceFeeAmount = clamp(discountedSubtotal * (svcPct / 100));
  const providerCommission = clamp(discountedSubtotal * (commPct / 100));

  const finalTotal = clamp(discountedSubtotal + taxAmount + serviceFeeAmount);

  // ── Human-readable breakdown ──────────────────────────────────────────────
  const breakdown: string[] = [];
  breakdown.push(`Base (${rule.model}): ${currency} ${baseAmount.toLocaleString()}`);
  if (seasonalSurcharge > 0) breakdown.push(`Seasonal surcharge: ${currency} ${seasonalSurcharge.toLocaleString()}`);
  if (weekendSurcharge > 0) breakdown.push(`Weekend surcharge: ${currency} ${weekendSurcharge.toLocaleString()}`);
  if (peakSurcharge > 0) breakdown.push(`Peak surcharge: ${currency} ${peakSurcharge.toLocaleString()}`);
  if (extraBedCharge > 0) breakdown.push(`Extra bed(s): ${currency} ${extraBedCharge.toLocaleString()}`);
  if (extraGuestCharge > 0) breakdown.push(`Extra guest(s): ${currency} ${extraGuestCharge.toLocaleString()}`);
  if (driverCharge > 0) breakdown.push(`Driver charge: ${currency} ${driverCharge.toLocaleString()}`);
  if (nightSurchargeAmount > 0) breakdown.push(`Night surcharge: ${currency} ${nightSurchargeAmount.toLocaleString()}`);
  if (tollCharge > 0) breakdown.push(`Toll: ${currency} ${tollCharge.toLocaleString()}`);
  if (parkingCharge > 0) breakdown.push(`Parking: ${currency} ${parkingCharge.toLocaleString()}`);
  if (waitingCharge > 0) breakdown.push(`Waiting: ${currency} ${waitingCharge.toLocaleString()}`);
  if (returnTripDiscount > 0) breakdown.push(`Return trip discount: -${currency} ${returnTripDiscount.toLocaleString()}`);
  addOnsDetail.forEach(a => breakdown.push(`${a.label}: ${currency} ${a.amount.toLocaleString()}`));
  if (totalDiscount > 0) breakdown.push(`Discount: -${currency} ${totalDiscount.toLocaleString()}`);
  breakdown.push(`Tax (${taxPct}%): ${currency} ${taxAmount.toLocaleString()}`);
  breakdown.push(`Service fee (${svcPct}%): ${currency} ${serviceFeeAmount.toLocaleString()}`);
  breakdown.push(`Total: ${currency} ${finalTotal.toLocaleString()}`);

  return {
    baseAmount,
    seasonalSurcharge,
    weekendSurcharge,
    peakSurcharge,
    extraBedCharge,
    extraGuestCharge,
    driverCharge,
    nightSurchargeAmount,
    tollCharge,
    parkingCharge,
    waitingCharge,
    returnTripDiscount,
    addOnsTotal,
    addOnsDetail,
    subtotal,
    couponDiscount,
    manualDiscount,
    totalDiscount,
    discountedSubtotal,
    taxAmount,
    taxPercent: taxPct,
    serviceFeeAmount,
    serviceFeePercent: svcPct,
    providerCommission,
    providerCommissionPercent: commPct,
    finalTotal,
    currency,
    priceModel: rule.model,
    breakdown,
  };
}

// ── Coupon validation (client-side preview; always revalidate server-side) ────
const DEMO_COUPONS: Record<string, { type: 'percent' | 'fixed'; value: number }> = {
  'NEPAL10': { type: 'percent', value: 10 },
  'TREK15': { type: 'percent', value: 15 },
  'WELCOME500': { type: 'fixed', value: 500 },
  'YATRA20': { type: 'percent', value: 20 },
};

export function validateCoupon(code: string, subtotal: number): CouponResult {
  const upper = code.trim().toUpperCase();
  const coupon = DEMO_COUPONS[upper];
  if (!coupon) {
    return { code, discountType: 'percent', discountValue: 0, discountAmount: 0, valid: false, message: 'Invalid coupon code' };
  }
  const discountAmount = coupon.type === 'percent'
    ? Math.round(subtotal * (coupon.value / 100))
    : coupon.value;
  return {
    code: upper,
    discountType: coupon.type,
    discountValue: coupon.value,
    discountAmount,
    valid: true,
    message: coupon.type === 'percent' ? `${coupon.value}% off applied` : `NPR ${coupon.value} off applied`,
  };
}

// ── Booking reference generator ───────────────────────────────────────────────
export function generateBookingRef(serviceType: ServiceType): string {
  const prefix: Record<ServiceType, string> = {
    package: 'PKG', hotel: 'HTL', guide: 'GDE', vehicle: 'VHL',
    flight: 'FLT', bus: 'BUS', train: 'TRN', event: 'EVT', restaurant: 'RST',
  };
  const ts = Date.now().toString(36).toUpperCase();
  const rand = Math.random().toString(36).slice(2, 6).toUpperCase();
  return `YN-${prefix[serviceType] || 'BKG'}-${ts}-${rand}`;
}

// ── Canonical booking status transitions ──────────────────────────────────────
export type BookingStatus = 'draft' | 'pending' | 'confirmed' | 'in_progress' | 'completed' | 'cancelled' | 'failed';

const VALID_TRANSITIONS: Record<BookingStatus, BookingStatus[]> = {
  draft:       ['pending', 'cancelled'],
  pending:     ['confirmed', 'cancelled', 'failed'],
  confirmed:   ['in_progress', 'cancelled'],
  in_progress: ['completed', 'cancelled'],
  completed:   [],
  cancelled:   [],
  failed:      ['pending'],
};

export function isValidStatusTransition(from: BookingStatus, to: BookingStatus): boolean {
  return VALID_TRANSITIONS[from]?.includes(to) ?? false;
}
